import { FC, memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Handle, NodeProps, Position } from '@xyflow/react';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WebhookIcon from '@mui/icons-material/Webhook';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BuildIcon from '@mui/icons-material/Build';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import LoopIcon from '@mui/icons-material/Loop';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TableRowsIcon from '@mui/icons-material/TableRows';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TableChartIcon from '@mui/icons-material/TableChart';
import StorageIcon from '@mui/icons-material/Storage';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import BoltIcon from '@mui/icons-material/Bolt';

interface NodeData {
  label?: string;
  type?: string;
  triggerType?: string;
  toolName?: string;
  parserId?: string;
  mcpToolName?: string;
}

const COLORS: Record<string, { accent: string; bg: string; border: string }> = {
  // eslint-disable-next-line camelcase
  trigger: { accent: '#00734D', bg: 'rgba(0,115,77,0.06)', border: 'rgba(0,115,77,0.18)' },
  // eslint-disable-next-line camelcase
  tool_call: { accent: '#27272A', bg: 'rgba(39,39,42,0.06)', border: 'rgba(39,39,42,0.18)' },
  // eslint-disable-next-line camelcase
  ai_call: { accent: '#6366F1', bg: 'rgba(99,102,241,0.06)', border: 'rgba(99,102,241,0.18)' },
  condition: { accent: '#CA8A04', bg: 'rgba(202,138,4,0.08)', border: 'rgba(202,138,4,0.18)' },
  loop: { accent: '#7C3AED', bg: 'rgba(124,58,237,0.06)', border: 'rgba(124,58,237,0.18)' },
  transform: { accent: '#71717A', bg: 'rgba(113,113,122,0.06)', border: 'rgba(113,113,122,0.18)' },
  // eslint-disable-next-line camelcase
  field_mapping: { accent: '#0EA5E9', bg: 'rgba(14,165,233,0.06)', border: 'rgba(14,165,233,0.18)' },
  merge: { accent: '#E86E44', bg: 'rgba(232,110,68,0.06)', border: 'rgba(232,110,68,0.18)' },
  visualization: { accent: '#0D9488', bg: 'rgba(13,148,136,0.06)', border: 'rgba(13,148,136,0.18)' },
  data: { accent: '#9333EA', bg: 'rgba(147,51,234,0.06)', border: 'rgba(147,51,234,0.18)' },
  parse: { accent: '#DB2777', bg: 'rgba(219,39,119,0.06)', border: 'rgba(219,39,119,0.18)' },
};

const DEFAULT_COLOR = COLORS.transform;

function colorFor(type?: string) {
  if (!type) return DEFAULT_COLOR;
  return COLORS[type] ?? DEFAULT_COLOR;
}

function iconFor(data: NodeData) {
  if (data.type === 'trigger') {
    if (data.triggerType === 'cron') return <ScheduleIcon fontSize="small" />;
    if (data.triggerType === 'webhook') return <WebhookIcon fontSize="small" />;
    if (data.triggerType === 'mcp') return <BoltIcon fontSize="small" />;
    return <PlayArrowIcon fontSize="small" />;
  }
  if (data.type === 'tool_call') return <BuildIcon fontSize="small" />;
  if (data.type === 'ai_call') return <PsychologyIcon fontSize="small" />;
  if (data.type === 'condition') return <CallSplitIcon fontSize="small" />;
  if (data.type === 'loop') return <LoopIcon fontSize="small" />;
  if (data.type === 'transform') return <SwapHorizIcon fontSize="small" />;
  if (data.type === 'field_mapping') return <TableRowsIcon fontSize="small" />;
  if (data.type === 'merge') return <MergeTypeIcon fontSize="small" />;
  if (data.type === 'visualization') return <TableChartIcon fontSize="small" />;
  if (data.type === 'data') return <StorageIcon fontSize="small" />;
  if (data.type === 'parse') return <AltRouteIcon fontSize="small" />;
  return <BuildIcon fontSize="small" />;
}

function subtitleFor(data: NodeData): string | undefined {
  if (data.type === 'trigger') {
    if (data.mcpToolName) return `mcp · ${data.mcpToolName}`;
    return data.triggerType;
  }
  if (data.type === 'tool_call' && data.toolName) return data.toolName;
  if (data.type === 'parse' && data.parserId) return data.parserId;
  return data.type;
}

const WorkflowDiagramNode: FC<NodeProps> = memo(({ data, selected }) => {
  const nodeData = data as NodeData;
  const colors = colorFor(nodeData.type);
  const subtitle = subtitleFor(nodeData);

  return (
    <Box
      sx={{
        'width': 240,
        'minHeight': 64,
        'borderRadius': '14px',
        'background': 'rgba(255,255,255,0.94)',
        'border': selected
          ? `2px solid ${colors.accent}`
          : '1px solid rgba(0,0,0,0.06)',
        'boxShadow': selected
          ? `0 0 0 3px ${colors.border}, 0 8px 24px rgba(0,0,0,0.10)`
          : '0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
        'overflow': 'visible',
        'position': 'relative',
        'transition': 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
          transform: 'translateY(-1px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderRadius: '14px 0 0 14px',
          background: `linear-gradient(180deg, ${colors.accent}, ${colors.accent}88)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, pl: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${colors.bg}, ${colors.border})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.accent,
            flexShrink: 0,
            boxShadow: `0 2px 6px ${colors.border}`,
          }}
        >
          {iconFor(nodeData)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: 12.5,
              color: 'rgba(0,0,0,0.85)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
          >
            {nodeData.label || nodeData.type || 'Node'}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0,0,0,0.4)',
                lineHeight: 1.3,
                fontSize: 10.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
                letterSpacing: '-0.02em',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 10,
          height: 10,
          background: '#fff',
          border: `2px solid ${colors.accent}`,
          boxShadow: '0 0 0 2px rgba(255,255,255,0.9), 0 2px 4px rgba(0,0,0,0.08)',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 10,
          height: 10,
          background: '#fff',
          border: `2px solid ${colors.accent}`,
          boxShadow: '0 0 0 2px rgba(255,255,255,0.9), 0 2px 4px rgba(0,0,0,0.08)',
        }}
      />
    </Box>
  );
});

WorkflowDiagramNode.displayName = 'WorkflowDiagramNode';

export default WorkflowDiagramNode;

export const workflowDiagramNodeTypes = {
  workflowNode: WorkflowDiagramNode,
};
